import React, { useState, useRef, useMemo, useCallback } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { startPaymentAndAwaitConfirmation } from '../lib/payments';
import { getPaymentElementOptions } from '../utils/stripePaymentElementOptions';

const StripeCardForm = ({ orderId, clientSecret, userEmail, billingDetails, onSuccess, onError, onProcessing }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [elementReady, setElementReady] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const submitLockRef = useRef(false);

  const paymentElementOptions = useMemo(
    () => getPaymentElementOptions(userEmail),
    [userEmail]
  );

  const handlePaymentElementLoadError = useCallback((event) => {
    console.error('[StripeCardForm] PaymentElement load error', event);
    const msg =
      event?.error?.message ||
      'No se pudo cargar el formulario de pago. En local usa HTTPS o prueba otro navegador (Chrome).';
    setLoadError(msg);
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      console.error("[StripeCardForm] Stripe or Elements not initialized");
      return;
    }

    if (submitLockRef.current) return;
    submitLockRef.current = true;

    setLoading(true);
    setError(null);
    let keepLoadingAfterSubmit = false;

    try {
      if (process.env.NODE_ENV === "development") {
        console.log("[StripeCardForm] Submitting payment...");
      }

      const { error: submitError } = await elements.submit();
      if (submitError) {
        console.error("[StripeCardForm] Validation error:", submitError);
        setError(submitError.message);
        return;
      }

      if (process.env.NODE_ENV === "development") {
        console.log("[StripeCardForm] Calling startPaymentAndAwaitConfirmation");
      }
      const result = await startPaymentAndAwaitConfirmation({
        orderId,
        method: "card",
        billingDetails: billingDetails || { email: userEmail },
        stripe,
        elements,
        clientSecret,
        onProcessing: typeof onProcessing === "function" ? onProcessing : undefined,
        onError: (msg) => {
          console.error("[StripeCardForm] Payment error callback:", msg);
          setError(msg);
          if (typeof onError === "function") onError(msg);
        },
      });

      if (process.env.NODE_ENV === "development") {
        console.log("[StripeCardForm] Result:", result);
      }

      if (result?.status === "processing") {
        if (typeof onSuccess === "function") {
          if (process.env.NODE_ENV === "development") {
            console.log("[StripeCardForm] calling onSuccess");
          }
          onSuccess(result.order);
          keepLoadingAfterSubmit = true;
        } else if (process.env.NODE_ENV === "development") {
          console.log(
            "[StripeCardForm] processing: onSuccess omitted; releasing lock (succeeded and processing both map here)"
          );
        }
      } else if (result?.status === "redirecting" && typeof onSuccess === "function") {
        keepLoadingAfterSubmit = true;
      } else if (result?.status === "redirecting" && process.env.NODE_ENV === "development") {
        console.log("[StripeCardForm] redirecting: onSuccess omitted; releasing lock");
      }
    } catch (err) {
      const msg = err?.message || "Error al procesar el pago";
      console.error("[StripeCardForm] handleSubmit error:", err);
      setError(msg);
      if (typeof onError === "function") onError(msg);
    } finally {
      if (!keepLoadingAfterSubmit) {
        setLoading(false);
        submitLockRef.current = false;
      }
    }
  };

  if (loadError) {
    return (
      <div className="alert alert-danger" role="alert">
        <strong>No se pudo cargar Stripe</strong>
        <p className="mb-2 small">{loadError}</p>
        <p className="mb-0 small text-muted">
          Si estás en <code>http://localhost</code>, prueba{' '}
          <code>HTTPS</code> (p. ej. mkcert) o desactiva bloqueo de cookies de terceros para{' '}
          <code>js.stripe.com</code>.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-3 border p-3 rounded bg-white" style={{ minHeight: '120px' }}>
        <PaymentElement
          options={paymentElementOptions}
          onReady={() => setElementReady(true)}
          onLoadError={handlePaymentElementLoadError}
        />
      </div>
      {!elementReady && !error && (
        <p className="small text-muted text-center mb-2">Cargando formulario de pago…</p>
      )}
      {error && <div className="alert alert-danger">{error}</div>}
      <button
        type="submit"
        disabled={!stripe || !elements || !elementReady || loading}
        className="btn btn-primary w-100 py-2"
      >
        {loading ? 'Procesando...' : 'Pagar'}
      </button>
      <div className="mt-2 text-center">
        <small className="text-muted">Pagos seguros con Stripe SSL</small>
      </div>
    </form>
  );
};

export default StripeCardForm;
